import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["attach"];
import { decorate } from './definition/decorator';
export const createResolverRegistry = ({
  httpClient,
  globalDecorators
}) => {
  const registeredResolvers = new Map();
  const register = ({
    entity,
    resolver
  }) => {
    var _registeredResolvers$;
    const existingRegistration = registeredResolvers.get(entity.typeName);
    if ((existingRegistration === null || existingRegistration === void 0 ? void 0 : existingRegistration.resolver) === resolver) {
      // No-op on duplicate registration
      return existingRegistration;
    }
    const registration = (_registeredResolvers$ = registeredResolvers.get(entity.typeName)) !== null && _registeredResolvers$ !== void 0 ? _registeredResolvers$ : {
      resolver,
      attachments: new Map()
    };

    // Update the resolver in case it has changed
    registration.resolver = globalDecorators.reduce((decorated, decorator) => decorated.with(decorator), decorate(resolver));

    // Update the registration
    registeredResolvers.set(entity.typeName, registration);
    for (const attachment of [...registration.attachments.values()]) {
      attachment.lifecycle.detach();
      attachment.lifecycle = resolver.attach(attachment.params, attachment.runtime);

      // If there was a request pending, immediately fire a refresh
      if (attachment.runtime.getState().loading) {
        attachment.lifecycle.refresh();
      }
    }
    return registration;
  };
  const getOrRegister = entity => registeredResolvers.get(entity.typeName) || register({
    entity,
    resolver: entity.defaultResolver
  });
  return {
    register,
    isAttached: ({
      instanceKey,
      entity
    }) => getOrRegister(entity).attachments.has(instanceKey),
    attach: ({
      instanceKey,
      entity,
      params,
      onResolverUpdate,
      getState
    }) => {
      const {
        resolver,
        attachments
      } = getOrRegister(entity);
      const attachment = attachments.get(instanceKey);
      if (attachment) {
        throw Error(`Duplicate attachment for ${instanceKey}`);
      }
      const runtime = {
        next: onResolverUpdate,
        getState,
        env: {
          httpClient,
          entity,
          instanceKey
        }
      };
      attachments.set(instanceKey, {
        params,
        runtime,
        lifecycle: resolver.attach(params, runtime)
      });
    },
    detach: ({
      instanceKey,
      entity
    }) => {
      const {
        attachments
      } = getOrRegister(entity);
      const attachment = attachments.get(instanceKey);
      if (!attachment) {
        throw Error(`Attempting to detach ${instanceKey} but no attachment found`);
      }
      attachment.lifecycle.detach();
      attachments.delete(instanceKey);
    },
    refresh: ({
      instanceKey,
      entity
    }) => {
      const {
        attachments
      } = getOrRegister(entity);
      const attachment = attachments.get(instanceKey);
      if (!attachment) {
        throw Error(`Attempting to refresh ${instanceKey} but no attachment found`);
      }
      attachment.lifecycle.refresh();
    },
    __devtools: {
      resolverRegistry: {
        getAttachedEntities: () => {
          const attachedEntities = [];
          for (const registration of registeredResolvers.values()) {
            for (const instanceKey of registration.attachments.keys()) {
              attachedEntities.push(instanceKey);
            }
          }
          return attachedEntities;
        },
        getRegisteredResolvers: () => {
          const resolvers = {};
          for (const _ref of registeredResolvers.entries()) {
            const [typeName, {
              resolver: _ref2
            }] = _ref;
            const rest = _objectWithoutPropertiesLoose(_ref2, _excluded);
            resolvers[typeName] = rest;
          }
          return resolvers;
        }
      }
    }
  };
};